import type {
  CanonicalWork,
  Contributor,
  Curation,
  SearchHit,
  Series,
} from "./generated/contracts";

export const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ||
  "https://eb-library-api.dvg-0fd.workers.dev"
).replace(/\/$/, "");

export type HomeShelf = Pick<
  Curation,
  "id" | "slug" | "title" | "description" | "items"
>;
export type ArchiveHome = {
  shelves: HomeShelf[];
  browse: {
    contributors: Array<{ slug: string; name: string; itemCount: number }>;
    bibleBooks: Array<{ book: string; itemCount: number }>;
    eras: Array<{ decade: number; itemCount: number }>;
  };
};

export type SearchFilters = {
  topic?: string;
  chapter?: string;
  verse?: string;
  sort?: string;
  medium?: string;
  series?: string;
  contributor?: string;
  book?: string;
  decade?: string;
  durationMin?: string;
  durationMax?: string;
  availability?: string;
};

function absolute(url: string) {
  return url.startsWith("/") ? `${API_BASE}${url}` : url;
}

function artwork(url?: string | null) {
  if (!url) return null;
  return url.startsWith("/artwork/") ? url : absolute(url);
}

function normalizeWork(work: CanonicalWork): CanonicalWork {
  return {
    ...work,
    artworkUrl: artwork(work.artworkUrl),
    assets: work.assets.map((asset) => ({
      ...asset,
      url: absolute(asset.url),
    })),
    manifestations: (work.manifestations || []).map((source) => ({
      ...source,
      assets: source.assets.map((asset) => ({
        ...asset,
        url: absolute(asset.url),
      })),
    })),
    relations: (work.relations || []).map((relation) => ({
      ...relation,
      work: normalizeWork(relation.work),
    })),
  };
}

async function request<T>(path: string): Promise<T> {
  for (let attempt = 0; attempt < 3; attempt++) {
    let response: Response | undefined;
    try {
      response = await fetch(
        `${API_BASE}${path}`,
        attempt ? { cache: "reload" } : undefined,
      );
    } catch {
      /* Retry a dropped read-only connection. */
    }
    if (response?.ok) return response.json() as Promise<T>;
    if (response && response.status < 500 && response.status !== 429)
      throw new Error(`This library page is unavailable (${response.status}).`);
    if (attempt < 2)
      await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
  }
  throw new Error("The library is temporarily unavailable. Please try again.");
}

export async function getHome(): Promise<ArchiveHome> {
  const data = await request<ArchiveHome>("/api/v2/archive/home");
  return {
    ...data,
    shelves: data.shelves.map((s) => ({
      ...s,
      items: s.items.map(normalizeWork),
    })),
  };
}

export type Facet = { value: string; label?: string; count: number };
export type SearchResponse = {
  results: SearchHit[];
  total: number;
  nextCursor: string | null;
  facets: {
    media: Facet[];
    topics: Facet[];
    contributors: Facet[];
    books: Facet[];
  };
};
export async function searchArchive(
  query: string,
  filters: SearchFilters = {},
  cursor?: string | null,
): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query });
  if (cursor) params.set("cursor", cursor);
  for (const [key, value] of Object.entries(filters))
    if (value)
      params.set(
        (
          {
            durationMin: "duration_min",
            durationMax: "duration_max",
          } as Record<string, string>
        )[key] || key,
        value,
      );
  const result = await request<SearchResponse>(
    `/api/v2/archive/search?${params}`,
  );
  return {
    ...result,
    results: result.results.map((hit) => ({
      ...hit,
      work: normalizeWork(hit.work),
    })),
  };
}
export type ReadingContent = {
  title: string;
  segments: Array<{ id: string; text: string; position: number }>;
  total: number;
  nextCursor: string | null;
};
export const getReading = (
  slug: string,
  cursor?: string | null,
  position?: number,
) =>
  request<ReadingContent>(
    `/api/v2/archive/works/${encodeURIComponent(slug)}/reading${position != null ? `?position=${position}` : cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
  );

export async function getWork(
  slug: string,
): Promise<CanonicalWork & { related?: CanonicalWork[] }> {
  const work = await request<CanonicalWork & { related?: CanonicalWork[] }>(
    `/api/v2/archive/works/${encodeURIComponent(slug)}`,
  );
  return {
    ...normalizeWork(work),
    related: (work.related || []).map(normalizeWork),
  };
}
export async function getSeries(
  slug: string,
  cursor?: string | null,
  aroundWork?: string | null,
): Promise<Series> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (aroundWork) params.set("around", aroundWork);
  const series = await request<Series>(
    `/api/v2/archive/series/${encodeURIComponent(slug)}${params.size ? "?" + params : ""}`,
  );
  return { ...series, items: series.items.map(normalizeWork) };
}
export type ArchivePerson = Contributor & {
  items: CanonicalWork[];
  totalCount?: number | null;
  nextCursor?: string | null;
};
export async function getPerson(
  slug: string,
  cursor?: string | null,
): Promise<ArchivePerson> {
  const person = await request<ArchivePerson>(
    `/api/v2/archive/people/${encodeURIComponent(slug)}${cursor ? "?cursor=" + encodeURIComponent(cursor) : ""}`,
  );
  return { ...person, items: person.items.map(normalizeWork) };
}
export async function getCuration(
  slug: string,
  cursor?: string | null,
): Promise<Curation> {
  const curation = await request<Curation>(
    `/api/v2/archive/curations/${encodeURIComponent(slug)}${cursor ? "?cursor=" + encodeURIComponent(cursor) : ""}`,
  );
  return { ...curation, items: curation.items.map(normalizeWork) };
}
